import { useState, useMemo } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Send, Loader2, Ruler, AlertTriangle, Clock, CheckCircle2 } from "lucide-react";
import { CONFIGURATOR_PRODUCTS, PRODUCT_OPTIONS, COLORS, EXTRAS } from "@/lib/configurator-data";
import { DIM_LIMITS } from "@/lib/pricing";
import { ElementPreview } from "@/components/ElementPreview";
import { SectionHeading } from "@/components/SectionHeading";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-white/5 border border-white/15 rounded-xl px-5 py-3.5 text-white placeholder:text-white/35 text-sm focus:outline-none focus:border-[#e2001a] focus:bg-white/10 transition-colors duration-200";

const STEP_LABELS = ["Produkt", "Maße", "Ausführung", "Farbe & Extras", "Anfrage"];

const StepDots = ({ step }) => (
  <div data-testid="config-steps" className="flex items-center gap-2 mb-8">
    {STEP_LABELS.map((label, i) => (
      <div key={label} className="flex items-center gap-2">
        <span className={`w-8 h-8 rounded-full flex items-center justify-center font-display font-semibold text-xs border transition-colors duration-300 ${
          i < step ? "bg-[#e2001a] border-[#e2001a] text-white" : i === step ? "border-[#e2001a] text-[#e2001a]" : "border-white/20 text-white/40"
        }`}>
          {i < step ? <Check size={13} /> : i + 1}
        </span>
        {i < STEP_LABELS.length - 1 && <span className={`w-6 sm:w-10 h-[2px] ${i < step ? "bg-[#e2001a]" : "bg-white/15"}`} />}
      </div>
    ))}
    <span className="ml-2 font-display text-[11px] uppercase tracking-[0.2em] text-white/45 hidden md:inline">{STEP_LABELS[step]}</span>
  </div>
);

const Choice = ({ selected, onClick, label, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`px-4 py-2.5 rounded-full border font-display text-sm font-medium transition-colors duration-200 ${
      selected ? "bg-[#e2001a] border-[#e2001a] text-white" : "border-white/20 text-white/70 hover:border-white/50 hover:text-white"
    }`}
  >
    {label}
  </button>
);

const DimInput = ({ label, value, min, max, onChange, testid }) => (
  <div>
    <div className="flex justify-between items-baseline mb-2">
      <label className="font-display text-white/70 text-sm">{label}</label>
      <span className="font-display text-white text-sm font-semibold">{value || "–"} mm</span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step="10"
      value={value || min}
      onChange={(e) => onChange(e.target.value)}
      data-testid={`${testid}-slider`}
      className="w-full accent-[#e2001a] mb-2"
    />
    <input
      data-testid={testid}
      type="number"
      min={min}
      max={max}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={inputCls}
    />
    <p className="text-white/30 text-[11px] mt-1.5">herstellbar: {min.toLocaleString("de-DE")}–{max.toLocaleString("de-DE")} mm</p>
  </div>
);

export const Configurator = () => {
  const [step, setStep] = useState(0);
  const [product, setProduct] = useState(null);
  const [dims, setDims] = useState({ width: "", height: "" });
  const [fence, setFence] = useState({ length: "", height: "1,4 m" });
  const [opts, setOpts] = useState({});
  const [color, setColor] = useState(COLORS[0]);
  const [extras, setExtras] = useState(["Montage durch Ringel-Team"]);
  const [note, setNote] = useState("");
  const [contact, setContact] = useState({ name: "", email: "", phone: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const options = product ? PRODUCT_OPTIONS[product.name] || [] : [];
  const isFence = product?.name === "Zäune";
  const limits = product ? DIM_LIMITS[product.name] : null;
  const fenceLength = parseFloat((fence.length || "").replace(",", "."));
  const [previewFlash, setPreviewFlash] = useState(false);

  const dimsValid = useMemo(() => {
    if (isFence) return fenceLength > 0;
    if (!limits) return false;
    const w = parseFloat(dims.width);
    const h = parseFloat(dims.height);
    return w >= limits.w[0] && w <= limits.w[1] && h >= limits.h[0] && h <= limits.h[1];
  }, [dims, limits, isFence, fenceLength]);

  const optionsReady = options.every((o) => opts[o.key]);

  const toggleExtra = (e) => setExtras((xs) => (xs.includes(e) ? xs.filter((x) => x !== e) : [...xs, e]));

  const pickProduct = (p) => {
    setProduct(p);
    setOpts({});
    const l = DIM_LIMITS[p.name];
    if (p.name === "Zäune") {
      setFence({ length: "25", height: "1,4 m" });
    } else if (l) {
      setDims({ width: String(l.dw), height: String(l.dh) });
    }
  };

  const sizeText = isFence
    ? `${fence.length || "–"} m · Höhe ${fence.height}`
    : `${dims.width || "–"} × ${dims.height || "–"} mm`;

  const flashPreview = () => {
    setPreviewFlash(true);
    setTimeout(() => setPreviewFlash(false), 1200);
  };

  const pickOpt = (key, value) => {
    setOpts({ ...opts, [key]: value });
    flashPreview();
  };

  const summaryLines = () => {
    const lines = [
      `Konfiguration: ${product.name}`,
      isFence ? `Länge: ${fence.length} m · Höhe: ${fence.height}` : `Maße: ${dims.width} × ${dims.height} mm (B × H)`,
      ...options.map((o) => `${o.label}: ${opts[o.key]}`),
      `Farbe: ${color}`,
      `Extras: ${extras.length ? extras.join(", ") : "keine"}`,
      `Gewünscht: Persönliches Angebot innerhalb von 24 Stunden`,
    ];
    if (note.trim()) lines.push(`Bemerkung: ${note.trim()}`);
    return lines;
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        name: contact.name,
        email: contact.email,
        phone: contact.phone,
        topic: product.name,
        message: summaryLines().join("\n"),
      });
      setDone(true);
      toast.success("Ihre Konfiguration wurde gesendet – wir melden uns mit einem Angebot.");
    } catch {
      toast.error("Senden fehlgeschlagen. Bitte erneut versuchen oder anrufen.");
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setStep(0); setProduct(null); setDims({ width: "", height: "" }); setFence({ length: "", height: "1,4 m" }); setOpts({});
    setColor(COLORS[0]); setExtras(["Montage durch Ringel-Team"]); setNote("");
    setContact({ name: "", email: "", phone: "" }); setDone(false);
  };

  const canNext = [!!product, dimsValid, optionsReady, true, true][step];

  return (
    <section id="konfigurator" data-testid="configurator-section" className="bg-[#1e2226] py-24 sm:py-32 border-y border-white/10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="mb-12">
          <SectionHeading
            dark
            testid="configurator"
            eyebrow="Konfigurator"
            title="Stellen Sie Ihr Bauelement zusammen."
            text="Produkt wählen, Maße eingeben, Ausführung und Farbe festlegen – die Vorschau aktualisiert sich live bis ins Detail. Ihr persönliches Angebot erhalten Sie kostenfrei innerhalb von 24 Stunden."
          />
        </div>

        <div className="grid lg:grid-cols-5 gap-6 items-start">
          {/* Live-Vorschau + Angebots-Versprechen */}
          <div className="lg:col-span-2 lg:sticky lg:top-28 space-y-4 order-first">
            <ElementPreview
              productName={product?.name || "Fenster"}
              color={color}
              opts={opts}
              hint={previewFlash}
              aspect={!isFence && parseFloat(dims.width) && parseFloat(dims.height) ? parseFloat(dims.width) / parseFloat(dims.height) : 1}
            />
            <div data-testid="offer-promise" className="relative bg-[#14171a] border border-white/10 rounded-2xl p-6 overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-[#e2001a] to-transparent" />
              <p className="font-display text-white/45 text-[11px] uppercase tracking-[0.22em] flex items-center gap-2">
                <Clock size={13} className="text-[#e2001a]" /> Ihr persönliches Angebot
              </p>
              <p className="font-display font-bold text-white text-xl mt-2">Kostenfrei & unverbindlich –<br />garantiert innerhalb von 24 Stunden.</p>
              <ul className="mt-4 space-y-2">
                {["Individuelle Maßanfertigung", "Persönliche Beratung vor Ort", "Exakte Planung zum Festpreis"].map((b) => (
                  <li key={b} className="flex items-center gap-2.5 text-white/60 text-sm">
                    <CheckCircle2 size={15} className="text-[#e2001a] shrink-0" /> {b}
                  </li>
                ))}
              </ul>
              <p className="text-white/35 text-[11px] mt-4">{product ? `${product.name} · ${sizeText}` : "Produkt wählen und konfigurieren"}</p>
            </div>
          </div>

          {/* Schritte */}
          <div className="lg:col-span-3 bg-[#14171a] border border-white/10 rounded-2xl p-6 sm:p-10">
            {!done && <StepDots step={step} />}

            <AnimatePresence mode="wait">
              {done ? (
                <motion.div
                  key="done"
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-testid="config-success"
                  className="text-center py-10"
                >
                  <span className="mx-auto w-16 h-16 rounded-full bg-[#e2001a] flex items-center justify-center mb-7">
                    <Check size={28} className="text-white" />
                  </span>
                  <h3 className="font-display font-bold text-white text-2xl sm:text-3xl">Anfrage gesendet!</h3>
                  <p className="text-white/60 mt-4 max-w-md mx-auto text-sm sm:text-base leading-relaxed">
                    Vielen Dank, {contact.name.split(" ")[0]}. Ihre Konfiguration ({product.name}, {sizeText}) ist bei uns eingegangen.
                    Wir melden uns innerhalb von 24 Stunden mit Ihrem persönlichen Angebot.
                  </p>
                  <button data-testid="config-reset" onClick={reset} className="mt-9 inline-flex items-center gap-2 border border-white/25 hover:border-white text-white font-display font-medium px-7 py-3.5 rounded-full transition-colors duration-300">
                    Neue Konfiguration starten
                  </button>
                </motion.div>
              ) : step === 0 ? (
                <motion.div key="s0" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.3 }}>
                  <div data-testid="config-products" className="grid grid-cols-2 sm:grid-cols-3 gap-3.5">
                    {CONFIGURATOR_PRODUCTS.map((p, i) => (
                      <button
                        type="button"
                        key={p.name}
                        data-testid={`config-product-${i}`}
                        onClick={() => pickProduct(p)}
                        className={`group relative overflow-hidden rounded-xl aspect-square border-2 transition-colors duration-300 ${
                          product?.name === p.name ? "border-[#e2001a]" : "border-transparent hover:border-white/30"
                        }`}
                      >
                        <img src={p.img} alt={p.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#14171a]/90 via-[#14171a]/10 to-transparent" />
                        {product?.name === p.name && (
                          <span className="absolute top-2.5 right-2.5 w-6 h-6 rounded-full bg-[#e2001a] flex items-center justify-center">
                            <Check size={13} className="text-white" />
                          </span>
                        )}
                        <span className="absolute bottom-3 left-3.5 right-3.5 text-left">
                          <span className="block font-display font-semibold text-white text-sm">{p.name}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                </motion.div>
              ) : step === 1 ? (
                <motion.div key="s1" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.3 }} className="space-y-8">
                  <p className="font-display text-white font-medium text-sm flex items-center gap-2"><Ruler size={15} className="text-[#e2001a]" /> {isFence ? "Länge & Höhe" : "Maße einstellen – die Vorschau reagiert live"}</p>
                  {isFence ? (
                    <div className="space-y-6">
                      <div>
                        <div className="flex justify-between items-baseline mb-2">
                          <label className="font-display text-white/70 text-sm">Zaunlänge</label>
                          <span className="font-display text-white text-sm font-semibold">{fence.length || "–"} m</span>
                        </div>
                        <input type="range" min="1" max="200" step="0.5" value={fenceLength || 1} onChange={(e) => setFence({ ...fence, length: e.target.value })} data-testid="config-fence-length-slider" className="w-full accent-[#e2001a] mb-2" />
                        <input data-testid="config-fence-length" type="number" min="1" max="500" step="0.5" value={fence.length} onChange={(e) => setFence({ ...fence, length: e.target.value })} placeholder="Länge in Metern *" className={inputCls} />
                      </div>
                      <div className="flex flex-wrap gap-2.5">
                        {["1,0 m", "1,4 m", "1,8 m", "2,0 m"].map((h) => (
                          <Choice key={h} testid={`config-fence-height-${h.replace(/[^a-zA-Z0-9]/g, "-")}`} label={`Höhe ${h}`} selected={fence.height === h} onClick={() => setFence({ ...fence, height: h })} />
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="grid sm:grid-cols-2 gap-8">
                      <DimInput label="Breite" value={dims.width} min={limits.w[0]} max={limits.w[1]} onChange={(v) => setDims({ ...dims, width: v })} testid="config-width" />
                      <DimInput label="Höhe" value={dims.height} min={limits.h[0]} max={limits.h[1]} onChange={(v) => setDims({ ...dims, height: v })} testid="config-height" />
                    </div>
                  )}
                  {!dimsValid && (dims.width || dims.height) && !isFence && (
                    <p data-testid="dims-warning" className="flex items-center gap-2 text-amber-300 text-sm">
                      <AlertTriangle size={15} /> Diese Maße sind so nicht herstellbar – bitte innerhalb der Grenzen bleiben.
                    </p>
                  )}
                </motion.div>
              ) : step === 2 ? (
                <motion.div key="s2" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.3 }} className="space-y-8">
                  <p className="text-white/45 text-xs flex items-center gap-2 -mb-2">
                    <CheckCircle2 size={13} className="text-[#e2001a]" /> Jede Auswahl siehst du sofort in der Vorschau links – bis ins Detail.
                  </p>
                  {options.map((o) => (
                    <div key={o.key}>
                      <p className="font-display text-white font-medium text-sm mb-3">{o.label}</p>
                      <div data-testid={`config-optgroup-${o.key}`} className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {o.choices.map((c, ci) => {
                          const active = opts[o.key] === c;
                          return (
                            <motion.button
                              key={c}
                              type="button"
                              initial={{ opacity: 0, y: 12 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: ci * 0.05, duration: 0.25 }}
                              data-testid={`config-opt-${o.key}-${c.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase()}`}
                              onClick={() => pickOpt(o.key, c)}
                              className={`relative flex flex-col items-center justify-center text-center gap-1.5 p-4 min-h-[76px] rounded-xl border transition-all duration-300 ${
                                active
                                  ? "border-[#e2001a] bg-[#1a1d20] shadow-[0_0_15px_rgba(226,0,26,0.15)] ring-1 ring-[#e2001a]/50"
                                  : "border-white/5 bg-white/5 hover:bg-white/10 hover:border-white/20"
                              }`}
                            >
                              {active && (
                                <span className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[#e2001a] flex items-center justify-center">
                                  <Check size={11} className="text-white" />
                                </span>
                              )}
                              <span className={`font-display text-xs leading-tight ${active ? "text-white font-semibold" : "text-white/65"}`}>{c}</span>
                            </motion.button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </motion.div>
              ) : step === 3 ? (
                <motion.div key="s3" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.3 }} className="space-y-8">
                  <div>
                    <p className="font-display text-white font-medium text-sm mb-3">Farbe</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {COLORS.map((c) => (
                        <button
                          key={c}
                          type="button"
                          data-testid={`config-color-${c.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase()}`}
                          onClick={() => { setColor(c); flashPreview(); }}
                          className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-colors duration-200 ${
                            color === c ? "border-[#e2001a] bg-white/10" : "border-white/15 hover:border-white/40"
                          }`}
                        >
                          <span className="w-7 h-7 rounded-md border border-black/30 shrink-0" style={{ background: { "Anthrazitgrau RAL 7016": "#3f454a", "Weiß RAL 9016": "#f2f2ef", "Verkehrsgrau RAL 7042": "#969ca1", "Schwarz RAL 9005": "#1c1e20", "Holzdekor Golden Oak": "#8a5a2b", "Holzdekor Nussbaum": "#5a3a22" }[c] }} />
                          <span className="font-display text-white/80 text-xs leading-tight">{c}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="font-display text-white font-medium text-sm mb-3">Extras</p>
                    <div className="flex flex-wrap gap-2.5">
                      {EXTRAS.map((x) => (
                        <Choice key={x} testid={`config-extra-${x.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase()}`} label={x} selected={extras.includes(x)} onClick={() => toggleExtra(x)} />
                      ))}
                    </div>
                  </div>
                  <textarea data-testid="config-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Bemerkung (optional) – z. B. Denkmalschutz, Wunschtermin …" className={`${inputCls} resize-none`} />
                </motion.div>
              ) : (
                <motion.form key="s4" onSubmit={submit} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.3 }}>
                  <div data-testid="config-summary" className="bg-white/5 border border-white/10 rounded-xl p-6 mb-8">
                    <p className="font-display font-semibold text-white mb-1">{product.name}</p>
                    <p className="text-white/50 text-sm mb-4">{sizeText} · {color}</p>
                    <ul className="space-y-1.5 text-sm text-white/65">
                      {options.map((o) => (
                        <li key={o.key} className="flex justify-between gap-4"><span>{o.label}</span><span className="text-white text-right">{opts[o.key]}</span></li>
                      ))}
                      <li className="flex justify-between gap-4"><span>Extras</span><span className="text-white text-right">{extras.length ? extras.join(", ") : "keine"}</span></li>
                      <li className="flex justify-between gap-4 pt-2 mt-1 border-t border-white/10">
                        <span>Angebot</span>
                        <span data-testid="summary-offer" className="text-[#e2001a] font-display font-semibold text-right">Persönliches Angebot<span className="text-white/40 font-normal text-xs"> (innerhalb von 24 h)</span></span>
                      </li>
                    </ul>
                  </div>

                  <div className="grid sm:grid-cols-3 gap-4 mb-8">
                    <input data-testid="config-name" required value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} placeholder="Ihr Name *" className={inputCls} />
                    <input data-testid="config-email" required type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} placeholder="E-Mail *" className={inputCls} />
                    <input data-testid="config-phone" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} placeholder="Telefon (optional)" className={inputCls} />
                  </div>
                </motion.form>
              )}
            </AnimatePresence>

            {!done && (
              <div className="flex justify-between mt-8 pt-6 border-t border-white/10">
                <button
                  type="button"
                  data-testid="config-back"
                  onClick={() => setStep((s) => Math.max(0, s - 1))}
                  disabled={step === 0}
                  className="inline-flex items-center gap-2 border border-white/25 hover:border-white disabled:opacity-30 text-white font-display font-medium px-6 py-3.5 rounded-full transition-colors duration-300"
                >
                  <ArrowLeft size={16} /> Zurück
                </button>
                {step < 4 ? (
                  <button
                    type="button"
                    data-testid="config-next"
                    disabled={!canNext}
                    onClick={() => setStep((s) => s + 1)}
                    className="inline-flex items-center gap-2 bg-[#e2001a] hover:bg-[#b80016] disabled:opacity-40 disabled:cursor-not-allowed text-white font-display font-semibold px-8 py-3.5 rounded-full transition-colors duration-300"
                  >
                    Weiter <ArrowRight size={16} />
                  </button>
                ) : (
                  <button
                    data-testid="config-submit"
                    onClick={submit}
                    disabled={sending}
                    className="inline-flex items-center gap-2.5 bg-[#e2001a] hover:bg-[#b80016] disabled:opacity-60 text-white font-display font-semibold px-8 py-3.5 rounded-full transition-colors duration-300"
                  >
                    {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />}
                    {sending ? "Wird gesendet …" : "Anfrage senden"}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
